import { isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';
import type { EzremsRoomBindingScope } from './ezrems-room-binding';

export interface EzremsBindingTarget {
  branch_id: number;
  branch_code: string;
  project_code: string;
}
export interface EzremsBindingCandidate {
  resource_id: number;
  dong: string;
  floor: string;
  ho2: string;
  sales_kind: string;
}
export interface EzremsBindingContext {
  room_id: string;
  room_version: number;
  /** 현재 연결 차수; 0이면 미연결, 재설정마다 1 증가. */
  binding_revision: number;
  binding: (EzremsBindingTarget & Omit<EzremsBindingCandidate, 'sales_kind'>) | null;
  scope: EzremsRoomBindingScope;
  unit_ids: string[];
  targets: EzremsBindingTarget[];
  status: 'NOT_SELECTED' | 'SUCCEEDED';
  branch_id?: number | null;
  branch_code?: string | null;
  project_code?: string | null;
  candidates: EzremsBindingCandidate[];
  next_resource_id: number | null;
}
export type EzremsBindingSelection = EzremsBindingTarget & { after_resource_id?: number };
export const record = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
export const exact = (v: Record<string, unknown>, keys: readonly string[]) =>
  Object.keys(v).length === keys.length && keys.every((k) => Object.hasOwn(v, k));
export const positive = (v: unknown): v is number =>
  typeof v === 'number' && Number.isSafeInteger(v) && v > 0;
export const text = (v: unknown): v is string =>
  typeof v === 'string' &&
  Array.from(v).length > 0 &&
  Array.from(v).length <= 128 &&
  v === v.trim();
export const targetKeys = ['branch_id', 'branch_code', 'project_code'];
export const coordinateKeys = ['resource_id', 'dong', 'floor', 'ho2'];
export const revisions = ['room_topology_revision', 'property_version', 'type_version'];
export const target = (v: unknown): v is EzremsBindingTarget =>
  record(v) &&
  exact(v, targetKeys) &&
  positive(v.branch_id) &&
  text(v.branch_code) &&
  text(v.project_code);
export const coordinates = (v: Record<string, unknown>) =>
  positive(v.resource_id) && text(v.dong) && text(v.floor) && text(v.ho2);
export const sameTarget = (a: EzremsBindingTarget, b: EzremsBindingTarget) =>
  a.branch_id === b.branch_id &&
  a.branch_code === b.branch_code &&
  a.project_code === b.project_code;
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '현재 EZREMS 연결 정보와 후보를 확인하지 못했습니다.',
  });

function parse(
  value: unknown,
  roomId: string,
  selection?: EzremsBindingSelection,
): EzremsBindingContext {
  if (
    !record(value) ||
    !exact(value, [
      'room_id',
      'room_version',
      'binding_revision',
      'binding',
      'scope',
      'unit_ids',
      'targets',
      'status',
      ...targetKeys.filter((key) => Object.hasOwn(value, key)),
      'candidates',
      'next_resource_id',
    ]) ||
    !isUuid(value.room_id) ||
    value.room_id.toLowerCase() !== roomId.toLowerCase() ||
    !positive(value.room_version) ||
    !Number.isSafeInteger(value.binding_revision) ||
    (value.binding_revision as number) < 0 ||
    !record(value.scope) ||
    !exact(value.scope, ['property_id', 'room_type_id', ...revisions]) ||
    !isUuid(value.scope.property_id) ||
    !isUuid(value.scope.room_type_id) ||
    revisions.some((key) => !positive((value.scope as Record<string, unknown>)[key])) ||
    !Array.isArray(value.unit_ids) ||
    value.unit_ids.length === 0 ||
    !value.unit_ids.every(isUuid) ||
    new Set(value.unit_ids.map((id: string) => id.toLowerCase())).size !== value.unit_ids.length ||
    !Array.isArray(value.targets) ||
    !value.targets.every(target) ||
    new Set(value.targets.map((t) => JSON.stringify([t.branch_id, t.project_code]))).size !==
      value.targets.length ||
    !Array.isArray(value.candidates) ||
    value.candidates.length > 100 ||
    value.candidates.some(
      (c: unknown) =>
        !record(c) ||
        !exact(c, [...coordinateKeys, 'sales_kind']) ||
        !coordinates(c) ||
        typeof c.sales_kind !== 'string',
    )
  )
    throw invalid();
  if (
    value.binding_revision === 0
      ? value.binding !== null
      : !record(value.binding) ||
        !exact(value.binding, [...targetKeys, ...coordinateKeys]) ||
        !coordinates(value.binding) ||
        !positive(value.binding.branch_id) ||
        !text(value.binding.branch_code) ||
        !text(value.binding.project_code)
  )
    throw invalid();
  const data = value as unknown as EzremsBindingContext;
  if (selection === undefined) {
    if (
      data.status !== 'NOT_SELECTED' ||
      data.candidates.length ||
      data.next_resource_id !== null ||
      data.branch_id != null ||
      data.branch_code != null ||
      data.project_code != null
    )
      throw invalid();
    return data;
  }
  {
    if (
      data.status !== 'SUCCEEDED' ||
      data.branch_id !== selection.branch_id ||
      data.branch_code !== selection.branch_code ||
      data.project_code !== selection.project_code ||
      !data.targets.some((t) => sameTarget(t, selection))
    )
      throw invalid();
    let previous = selection.after_resource_id ?? 0;
    for (const candidate of data.candidates) {
      if (candidate.resource_id <= previous) throw invalid();
      previous = candidate.resource_id;
    }
    if (
      data.next_resource_id !== null &&
      (!positive(data.next_resource_id) ||
        data.candidates.length !== 100 ||
        data.next_resource_id !== previous)
    )
      throw invalid();
  }
  return data;
}

export async function readEzremsBindingContext(
  client: ServiceClient,
  roomId: string,
  selection?: EzremsBindingSelection,
  signal?: AbortSignal,
): Promise<EzremsBindingContext> {
  if (
    !isUuid(roomId) ||
    (selection &&
      (!target({
        branch_id: selection.branch_id,
        branch_code: selection.branch_code,
        project_code: selection.project_code,
      }) ||
        (selection.after_resource_id !== undefined && !positive(selection.after_resource_id))))
  )
    throw invalid();
  const value = await client.get<unknown>(`admin/rooms/${roomId}/ezrems-binding-context`, {
    headers: { 'Cache-Control': 'no-store' },
    signal,
    ...(selection
      ? {
          params: {
            branch_id: selection.branch_id,
            project_code: selection.project_code,
            ...(selection.after_resource_id === undefined
              ? {}
              : { after_resource_id: selection.after_resource_id }),
          },
        }
      : {}),
  });
  return parse(value, roomId, selection);
}
